/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Styles for the onboarding splash screen.
 *
 * @file OnboardingSplashStyles.js
 * @author Gladson Samuel S
 * @since 2.0.0
 */

import { styled } from '@mui/material/styles';
import mixins from '../../../assets/styles/mixins.scss';
import { createScopedClasses, createScopedStyles } from '../../../assets/styles/createScopedStyles';

const wordWrap = {
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  whiteSpace: 'normal'
};

const useOnboardingSplashStyles = {
  shell: {
    backgroundColor: mixins.lightThemeBgColor2,
    borderRadius: 24,
    padding: 16,
    width: '100%',
    boxSizing: 'border-box'
  },
  layout: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%',
    '@media (min-width: 768px)': {
      gap: 32
    },
    '@media (min-width: 1024px)': {
      flexDirection: 'row',
      gap: 16,
      minHeight: 456
    }
  },
  layoutReverse: {
    '@media (min-width: 1024px)': {
      flexDirection: 'row-reverse'
    }
  },
  panelWide: {
    display: 'flex',
    minWidth: 0,
    '@media (min-width: 1024px)': {
      flex: '2 1 0'
    }
  },
  panelNarrow: {
    display: 'flex',
    minWidth: 0,
    '@media (min-width: 1024px)': {
      flex: '1 1 0'
    }
  },
  getStartedPanel: {
    backgroundColor: '#DAD8FF',
    borderRadius: 16,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    width: '100%',
    flex: 1,
    boxSizing: 'border-box',
    '@media (min-width: 768px)': {
      padding: 24
    },
    '@media (min-width: 1024px)': {
      padding: 40
    }
  },
  promotionPanel: {
    backgroundColor: mixins.chalkWhiteFont,
    borderRadius: 16,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    width: '100%',
    flex: 1,
    boxSizing: 'border-box',
    '@media (min-width: 768px)': {
      padding: 24
    },
    '@media (min-width: 1024px)': {
      padding: 40
    }
  },
  promotionBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    width: '100%'
  },
  panelTitle: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 18,
    fontWeight: mixins.fontWeight500,
    lineHeight: '25px',
    letterSpacing: '-0.4px',
    margin: 0,
    maxWidth: 760,
    ...wordWrap,
    '@media (min-width: 1024px)': {
      fontSize: mixins.fontSize20,
      lineHeight: '28px'
    }
  },
  getStartedTitle: {
    color: mixins.amethyst
  },
  promotionTitle: {
    color: mixins.color_text_primary
  },
  actionCardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: '100%'
  },
  actionCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    minWidth: 0,
    padding: 16,
    borderRadius: 12,
    border: 'none',
    backgroundColor: mixins.chalkWhiteFont,
    textAlign: 'left',
    cursor: 'pointer',
    boxSizing: 'border-box',
    transition: 'background-color 0.15s ease, align-items 0.15s ease',
    '@media (min-width: 390px)': {
      minWidth: 320
    },
    '@media (min-width: 1024px)': {
      gap: 12
    },
    '&:hover': {
      alignItems: 'flex-start',
      backgroundColor: mixins.amethystColorV2,
      '& $actionCardTitle': {
        color: '#E8E6F3'
      },
      '& $actionCardDescription': {
        color: '#E8E6F3'
      },
      '& $actionCardIconWrap': {
        backgroundColor: '#EFEDFF'
      }
    }
  },
  actionCardIconWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: mixins.amethyst,
    backgroundColor: '#EFEDFF',
    borderRadius: 1000,
    padding: 8,
    transition: 'background-color 0.15s ease',
    '& svg': {
      width: 32,
      height: 32
    },
    '@media (min-width: 1024px)': {
      padding: 12,
      '& svg': {
        width: 40,
        height: 40
      }
    }
  },
  actionCardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    flex: 1,
    minWidth: 0
  },
  actionCardTitleRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    minHeight: 32
  },
  actionCardTitle: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 18,
    fontWeight: mixins.fontWeight500,
    lineHeight: '25px',
    letterSpacing: '-0.4px',
    color: mixins.amethyst,
    margin: 0,
    transition: 'color 0.15s ease',
    ...wordWrap
  },
  actionCardDescription: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight500,
    lineHeight: '22px',
    color: mixins.amethyst,
    margin: 0,
    transition: 'color 0.15s ease',
    ...wordWrap
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2px 4px',
    borderRadius: 4,
    backgroundColor: '#E8E6F3',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    color: mixins.amethyst,
    flexShrink: 0
  },
  featureList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: '0 12px',
    margin: 0,
    listStyle: 'none',
    width: '100%',
    boxSizing: 'border-box'
  },
  featureItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8
  },
  featureIcon: {
    flexShrink: 0,
    display: 'flex',
    marginTop: 3,
    color: mixins.color_text_primary,
    '& svg': {
      width: 18,
      height: 18
    },
    '& svg path:last-child': {
      fill: mixins.chalkWhiteFont
    }
  },
  featureText: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: 400,
    lineHeight: '24px',
    color: mixins.color_text_primary,
    margin: 0,
    flex: 1,
    minWidth: 0,
    ...wordWrap
  },
  learnMoreButton: {
    appearance: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 1000,
    border: `2px solid ${mixins.navyColor}`,
    color: mixins.navyColor,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '16px',
    padding: '12px 20px',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    alignSelf: 'flex-start',
    fontFeatureSettings: '\'liga\' off',
    '&:hover': {
      backgroundColor: mixins.secondaryButtonHoverColor
    },
    '&:focus-visible': {
      outline: `2px solid ${mixins.navyColor}`,
      outlineOffset: 2
    }
  }
};

export const classes = createScopedClasses('aet-onboarding-splash', useOnboardingSplashStyles);

export const scopedStyles = createScopedStyles(useOnboardingSplashStyles, classes);

export const OnboardingSplashStylesRoot = styled('div')(scopedStyles);

export default useOnboardingSplashStyles;
